import {useQuery} from '@tanstack/react-query';
import type {MultiPolygon, Polygon} from 'geojson';
import {DateTime} from 'luxon';
import urls from './urls.js';
import {request} from './index.js';
import {INTERPOLATOR, MOSAICKING_ORDER} from '@src/constants/analyze.ts';

export type ProcessRequestBody = {
  evalscript: string;
  input: {
    bounds: {
      properties: {
        crs:
          | 'http://www.opengis.net/def/crs/EPSG/0/4326'
          | 'http://www.opengis.net/def/crs/EPSG/0/3857';
      };
    } & (
      | {bbox: [number, number, number, number]}
      | {geometry: MultiPolygon | Polygon}
    );
    data: {
      mosaickingOrder?: MOSAICKING_ORDER;
      type: string;
      dataFilter: {
        timeRange: {
          from: string;
          to: string;
        };
      };
      processing?: {
        downsampling?: INTERPOLATOR;
        upsampling?: INTERPOLATOR;
      };
    }[];
  };
  output: {
    responses: {
      identifier: string;
      format: {
        type: string;
      };
    }[];
  } & ({width: number; height: number} | {resx: number; resy: number});
};

const createProcessRequestBody = ({
  evalscript,
  collectionId,
  geometry,
  width = 100,
  height = 100,
  from = DateTime.now().startOf('day').toISO(),
  to = DateTime.now().endOf('day').toISO(),
}: ProcessRequestParameters): ProcessRequestBody => {
  const interpolatedBody: ProcessRequestBody = {
    evalscript,
    input: {
      bounds: {
        properties: {crs: 'http://www.opengis.net/def/crs/EPSG/0/4326'},
        geometry,
      },
      data: [
        {
          mosaickingOrder: MOSAICKING_ORDER.mostRecent,
          dataFilter: {
            timeRange: {from, to},
          },
          type: collectionId,
        },
      ],
    },
    output: {
      width,
      height,
      responses: [
        {
          identifier: 'default',
          format: {
            type: 'image/png',
          },
        },
      ],
    },
  };
  return interpolatedBody;
};

const getProcessImage = async (body: ProcessRequestBody): Promise<Blob> => {
  const response = await request.post(urls.process, body, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'image/png',
    },
    responseType: 'blob',
  });
  return await response.data;
};

type ProcessRequestParameters = {
  evalscript: string;
  collectionId: string;
  geometry: MultiPolygon | Polygon;
  width?: number;
  height?: number;
  from?: string;
  to?: string;
};

export const useGetProcessImage = (
  parameters: Partial<ProcessRequestParameters>,
) => {
  const {evalscript, collectionId, geometry} = parameters;
  return useQuery({
    queryKey: [evalscript, collectionId, geometry],
    queryFn: () => {
      const requestBody = createProcessRequestBody(
        parameters as ProcessRequestParameters,
      );
      return getProcessImage(requestBody);
    },
    enabled: Boolean(evalscript) && Boolean(collectionId) && Boolean(geometry),
    staleTime: Infinity,
  });
};
